import React, { useContext, useState } from 'react';
import { ChangeEvent } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { BsEyeSlash, BsEye } from 'react-icons/bs';
import { ContextUser } from '../../../providers/ContextUser';
import {
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  BsXCircleRed,
  BsCheckCircleGreen,
} from './styles';

const Recover = ({ token, firstAccess }) => {
  const navigate = useNavigate();
  const { handleReset, handleLogout } = useContext(ContextUser);
  const [requesting, setRequesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    hasMinimumLimit: false,
    hasLowercaseLetter: false,
    hasUppercaseLatter: false,
    hasNumber: false,
  });

  const passwordValidation = (value) => {
    const errors = {
      hasMinimumLimit: value.length >= 8,
      hasLowercaseLetter: /^(?=.*[a-z])/.test(value),
      hasUppercaseLatter: /^(?=.*[A-Z])/.test(value),
      hasNumber: /^(?=.*\d)/.test(value),
    };

    setPasswordErrors(errors);
    return (
      errors.hasMinimumLimit &&
      errors.hasLowercaseLetter &&
      errors.hasUppercaseLatter &&
      errors.hasNumber
    );
  };

  const formik = useFormik({
    initialValues: {
      password: '',
      password2: '',
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .required('Campo obrigatório')
        .test(
          'passwordValidation',
          'A senha não atende aos requisitos',
          passwordValidation
        ),
      password2: Yup.string()
        .oneOf([Yup.ref('password'), undefined], 'As senhas não coincidem')
        .required('Campo obrigatório'),
    }),
    onSubmit: (values) => {
      setRequesting(true);
      return handleReset(token, values.password).finally(() =>
        setRequesting(false)
      );
    },
  });

  const { values, handleChange, handleSubmit, errors, touched } = formik;

  const passwordValidIcon = (valid) =>
    valid ? <BsCheckCircleGreen /> : <BsXCircleRed />;

  return (
    <>
             <Box
         sx={{
           maxWidth: 400,
           width: '100%',
           background: 'rgba(255, 255, 255, 0.95)',
           backdropFilter: 'blur(20px)',
           padding: { xs: 2, md: 4 },
           borderRadius: 2,
           marginBottom: 2,
           boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
           border: '1px solid rgba(255, 255, 255, 0.2)',
           position: 'relative',
           zIndex: 1,
         }}
       >
        {/* Título do formulário */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#1a1a2e',
              mb: 1,
            }}
          >
            {firstAccess ? 'Primeiro acesso à SOLIS' : 'Redefinir senha'}
          </Typography>
          
          {firstAccess && (
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                fontSize: '16px',
                lineHeight: 1.5,
              }}
            >
              Bem-vindo à SOLIS! Para garantir a segurança de sua conta, 
              defina uma senha forte seguindo os requisitos abaixo.
            </Typography>
          )}
        </Box>
        <form onSubmit={handleSubmit} action=''>
          <FormControl fullWidth margin='normal' variant='outlined'>
            <InputLabel htmlFor='new-password' shrink={!!values.password}>Nova senha</InputLabel>
            <OutlinedInput
              id='new-password'
              type={showPassword ? 'text' : 'password'}
              name='password'
              value={values.password}
              onChange={(e) => {
                passwordValidation(e.target.value);
                handleChange(e);
              }}
              label='Nova senha'
              notched={!!values.password}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={() => setShowPassword(!showPassword)}
                    edge='end'
                  >
                    {showPassword ? <BsEye /> : <BsEyeSlash />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          
          <List sx={{ mt: 1, mb: 2 }}>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                {passwordValidIcon(passwordErrors.hasMinimumLimit)}
              </ListItemIcon>
              <ListItemText 
                primary="Mínimo 8 caracteres" 
                sx={{ '& .MuiListItemText-primary': { fontSize: '14px' } }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                {passwordValidIcon(passwordErrors.hasNumber)}
              </ListItemIcon>
              <ListItemText 
                primary="Números" 
                sx={{ '& .MuiListItemText-primary': { fontSize: '14px' } }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                {passwordValidIcon(passwordErrors.hasUppercaseLatter)}
              </ListItemIcon>
              <ListItemText 
                primary="Letras maiúsculas" 
                sx={{ '& .MuiListItemText-primary': { fontSize: '14px' } }}
              />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                {passwordValidIcon(passwordErrors.hasLowercaseLetter)}
              </ListItemIcon>
              <ListItemText 
                primary="Letras minúsculas" 
                sx={{ '& .MuiListItemText-primary': { fontSize: '14px' } }}
              />
            </ListItem>
          </List>

          <FormControl fullWidth margin='normal' variant='outlined'>
            <InputLabel htmlFor='confirm-password' shrink={!!values.password2}>Confirme a senha</InputLabel>
            <OutlinedInput
              id='confirm-password'
              type={showPassword2 ? 'text' : 'password'}
              name='password2'
              value={values.password2}
              onChange={handleChange}
              label='Confirme a senha'
              notched={!!values.password2}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={() => setShowPassword2(!showPassword2)}
                    edge='end'
                  >
                    {showPassword2 ? <BsEye /> : <BsEyeSlash />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          
          {touched.password2 && errors.password2 && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {errors.password2}
            </Typography>
          )}

          <Button
            id="submit-password-reset"
            type='submit'
            variant='contained'
            color='primary'
            fullWidth
            disabled={requesting}
            sx={{
              mt: 3,
              mb: 2,
            }}
          >
            {requesting ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Definir nova senha'
            )}
                     </Button>
         </form>

         {/* Botão Retornar */}
         <Box sx={{ textAlign: 'center', mt: 3 }}>
           <Button
             id="back-to-login"
             variant='text'
             color='primary'
             onClick={() => {
               handleLogout();
               navigate('/acessar');
             }}
             sx={{
               textTransform: 'none',
               fontSize: '14px',
               fontWeight: 500,
               p: 0,
               minWidth: 'auto',
             }}
           >
             ← Voltar ao login
           </Button>
         </Box>
       </Box>
     </>
   );
 };

export default Recover;
